import { setCookie, getCookie } from './utils.js';

// Initialize active navigation
export function initActiveNavigation() {
  const currentPath = window.location.pathname;
  const sidebarLinks = document.querySelectorAll('.sidebar-list-primary__link');
  
  // Remove active class from all items
  document.querySelectorAll('.sidebar-list-primary__item').forEach(item => {
    item.classList.remove('active');
  });
  
  // Check each link and add active class to the corresponding item
  sidebarLinks.forEach(link => {
    const href = link.getAttribute('href');
    const listItem = link.closest('.sidebar-list-primary__item');
    
    // Skip if href is empty or just '#'
    if (!href || href === '#') return;
    
    // Check if current path matches the link
    if (isPathActive(currentPath, href)) {
      listItem.classList.add('active');
    }
  });
}

function isPathActive(currentPath, linkPath) {
  // Exact match
  if (currentPath === linkPath) {
    return true;
  }
  
  // Special case for home page
  if (currentPath === '/' && linkPath === '/') {
    return true;
  }
  
  // If link is a parent path and current path starts with it
  // e.g., /articles matches /articles/some-post
  if (linkPath !== '/' && currentPath.startsWith(linkPath + '/')) {
    return true;
  }
  
  return false;
}

// Initialize secondary dropdown
export function initSecondaryDropdown() {
  const logoGroups = document.querySelectorAll('.logo-dropdown');
  if (!logoGroups.length) return;

  function hideAllDropdowns() {
    logoGroups.forEach((group) => {
      const dropdown = group.querySelector('.dropdown-secondary');
      if (dropdown) dropdown.classList.remove('show');
      group.classList.remove('active');
    });
  }

  function toggleForGroup(group) {
    const dropdown = group.querySelector('.dropdown-secondary');
    if (!dropdown) return;
    const isVisible = dropdown.classList.contains('show');
    // Close others first
    hideAllDropdowns();
    if (!isVisible) {
      dropdown.classList.add('show');
      group.classList.add('active');
    }
  }

  logoGroups.forEach((group) => {
    const dropdown = group.querySelector('.dropdown-secondary');
    if (!dropdown) return;

    const arrow = group.querySelector('.logo-dropdown-arrow-wrap');
    if (arrow) {
      arrow.addEventListener('click', (event) => {
        event.stopPropagation();
        toggleForGroup(group);
      });
    }

    dropdown.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  });

  document.addEventListener('click', (event) => {
    // If click is outside all groups/dropdowns, close all
    const clickedInsideSomeGroup = Array.from(logoGroups).some((group) => group.contains(event.target));
    if (!clickedInsideSomeGroup) {
      hideAllDropdowns();
    }
  });
}

// Mobile Sidebar toggle
export function initMobileSidebar() {
  const sidebar = document.querySelector('.sidebar');
  const menuButton = document.querySelector('.header-menu-button');
  const searchMenuButton = document.querySelector('.search-menu-button');
  if (!sidebar || !menuButton) return;

  const openIcon = menuButton.querySelector('.header-menu-icon--open');
  const closeIcon = menuButton.querySelector('.header-menu-icon--close');
  let isOpen = false;

  // Ensure initial icon state
  if (openIcon) openIcon.style.display = 'block';
  if (closeIcon) closeIcon.style.display = 'none';

  function swapIcons(open) {
    if (openIcon) openIcon.style.display = open ? 'none' : 'block';
    if (closeIcon) closeIcon.style.display = open ? 'block' : 'none';
  }

  function toggleSidebar() {
    isOpen = !isOpen;
    sidebar.classList.toggle('show', isOpen);
    document.body.classList.toggle('sidebar-open', isOpen);
    swapIcons(isOpen);
    if (isOpen && window.innerWidth <= 900) {
      document.dispatchEvent(new Event('menu-opened'));
    }
  }

  menuButton.addEventListener('click', (e) => {
    e.preventDefault();
    toggleSidebar();
  });

  // Search menu button: toggle sidebar without closing search
  if (searchMenuButton) {
    searchMenuButton.addEventListener('click', (e) => {
      e.preventDefault();
      toggleSidebar();
    });
  }

  document.addEventListener('search-opened', () => {
    if (isOpen && window.innerWidth <= 900) toggleSidebar();
  });

  // Close sidebar when clicking outside
  document.addEventListener('click', (e) => {
    if (isOpen && !sidebar.contains(e.target) && !menuButton.contains(e.target) && !searchMenuButton?.contains(e.target)) {
      toggleSidebar();
    }
  });

  // Close sidebar on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) {
      toggleSidebar();
    }
  });
}

// Sidebar collapse functionality
export function initSidebarCollapse() {
  const sidebar = document.querySelector('.sidebar');
  const toggleSidebarButton = document.querySelector('.toggle-sidebar-button');
  const containerGlobal = document.querySelector('.container-global');
  if (!sidebar || !toggleSidebarButton) return;

  const openIcon = toggleSidebarButton.querySelector('.toggle-sidebar-icon--open');
  const closedIcon = toggleSidebarButton.querySelector('.toggle-sidebar-icon--closed');

  function updateIcons(isCollapsed) {
    if (openIcon) openIcon.style.display = isCollapsed ? 'none' : 'block';
    if (closedIcon) closedIcon.style.display = isCollapsed ? 'block' : 'none';
    if (containerGlobal) {
      containerGlobal.classList.toggle('sidebar-collapsed', isCollapsed);
    }
    document.body.classList.toggle('sidebar-collapsed', isCollapsed);
  }

  function setSidebarState(isCollapsed) {
    if (isCollapsed) {
      sidebar.classList.add('sidebar--collapsed');
    } else {
      sidebar.classList.remove('sidebar--collapsed');
    }
    updateIcons(isCollapsed);
    setCookie('sidebar-collapsed', isCollapsed ? 'true' : 'false');
  }

  toggleSidebarButton.addEventListener('click', (e) => {
    e.preventDefault();
    const isCollapsed = sidebar.classList.toggle('sidebar--collapsed');
    updateIcons(isCollapsed);
    setCookie('sidebar-collapsed', isCollapsed ? 'true' : 'false');
    document.documentElement.classList.toggle('sidebar-collapsed-preload', isCollapsed);
  });

  // Check preloaded state from HTML class or cookie
  const hasPreloadClass = document.documentElement.classList.contains('sidebar-collapsed-preload');
  const savedState = getCookie('sidebar-collapsed');
  const isCollapsed = hasPreloadClass || savedState === 'true';
  
  // Remove preload class and apply actual state
  document.documentElement.classList.remove('sidebar-collapsed-preload');
  setSidebarState(isCollapsed);
}

